import { Button, Modal, message } from "antd";
import type { AddressInfo, DeliveryVo, OrderVo, ReceivePayment, ScheduleVo } from "@zhihaoscm/entity";
import {
    DELIVERY_STATE,
    DELIVERY_STATE_PC,
    ORDER_TYPE_PC,
    PC_RECEIVE_STATE,
    RECEIVE_PAYMENT_TYPE,
    SCHEDULE_STATE,
    SETTLEMENT_METHOD,
    TRANSPORT_ORDER_SHIP_STATE,
    TRADE_ORDER_STATE_PC
} from "@zhihaoscm/meta";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BaseItem, Button as ZhihaoButton, Grid, Pagination, Table, type TableColumnProps, type TableRef } from "zhihao-ui-react";
import { deliveryConfirm } from "@/api/deliveryService";
import { scheduleService } from "@/api/scheduleService";
import { EmptyState, InfoCard, ZhInsetTabs } from "@/components/common";
import { AddressMapDialog } from "./OrderLegacyDialogs";
import { OrderPaymentDialogs, type OrderPaymentAction } from "./OrderPaymentDialogs";
import { DeliveryInfo } from "./DeliveryInfo";
import { DeliveryLogisticsTracking } from "./DeliveryLogisticsTracking";
import { DeliveryProofMaterials } from "./DeliveryProofMaterials";
import "../delivery-detail.css";

const money = (value?: number) => Number(value ?? 0).toLocaleString("zh-CN", { maximumFractionDigits: 2 });
const addressText = (info?: Record<string, unknown>) =>
    info ? [info.province, info.city, info.district, info.detailAddress].filter(Boolean).join(" ") || "--" : "--";
const shipName = (ship?: { cnname?: string; name?: string }, id?: string) => ship?.cnname || ship?.name || id || "--";

/** 旧版仅允许在提货发起后的 48 小时有效期内手动确认。 */
function canConfirmDelivery(row: DeliveryVo) {
    return (
        row.delivery.state === DELIVERY_STATE.TO_BE_CONFIRMED.code &&
        Boolean(row.delivery.initiationTime) &&
        dayjs(row.delivery.initiationTime).add(48, "hour").isAfter(dayjs())
    );
}

/** 提货确认剩余时间沿用订单详情统一的“天+时:分:秒”格式。 */
function deliveryRemainingTime(value?: string | Date) {
    const remaining = value ? dayjs(value).add(48, "hour").valueOf() - Date.now() : 0;
    if (remaining <= 0) return "--";
    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${days ? `${days}天` : ""}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function Section({ children, id, title }: { children: React.ReactNode; id?: string; title: string }) {
    return (
        <section className="section-card order-detail-section" id={id}>
            <h2 className="section-title">{title}</h2>
            {children}
        </section>
    );
}

/** 迁移自旧 orderDetail/deliveryMessage.vue。 */
function OrderDeliveryMessage({ detail }: { detail: OrderVo }) {
    const order = detail.order;
    const period =
        order.deliveryStartTime || order.deliveryEndTime
            ? `${order.deliveryStartTime ? dayjs(order.deliveryStartTime).format("YYYY-MM-DD") : "--"} 至 ${order.deliveryEndTime ? dayjs(order.deliveryEndTime).format("YYYY-MM-DD") : "--"}`
            : "--";
    return (
        <section className="section-card order-detail-section">
            <InfoCard title="提货信息">
                <Grid cols={4} gap={16}>
                    <BaseItem label="提货周期" infoDirection="vertical" value={period} />
                    <BaseItem label="可提货吨数" infoDirection="vertical" value={`${money(order.ton)}吨`} />
                    <BaseItem
                        label="提货地点"
                        span={2}
                        infoDirection="vertical"
                        value={addressText(order.deliveryAddressInfo as unknown as Record<string, unknown>)}
                    />
                </Grid>
            </InfoCard>
        </section>
    );
}

/** 迁移自旧 orderDetail/scheduleCalendar.vue 与 scheduleList.vue。 */
function OrderScheduleCalendar({ detail, onRefresh }: { detail: OrderVo; onRefresh?: () => Promise<void> | void }) {
    const [page, setPage] = useState(1); const [listPage, setListPage] = useState(1); const [scheduleRows, setScheduleRows] = useState<ScheduleVo[]>([]); const [scheduleTotal, setScheduleTotal] = useState(0); const [paymentAction, setPaymentAction] = useState<OrderPaymentAction>(); const [mapAddress, setMapAddress] = useState<AddressInfo>();
    const order = detail.order;
    /** 旧 scheduleList 独立分页，不复用订单详情里的日历 Schedule 数组。 */
    const loadSchedules = async (currentPage = listPage) => { const result = await scheduleService.customPaging({ page: currentPage, size: 5, orderId: order.id }); const pageResult = result as unknown as { list: ScheduleVo[]; total: number }; setScheduleRows(pageResult.list ?? []); setScheduleTotal(pageResult.total ?? 0); };
    useEffect(() => { void loadSchedules(listPage); }, [listPage, order.id]);
    const start = dayjs(order.deliveryStartTime).isAfter(dayjs(order.createdTime), "day")
        ? dayjs(order.deliveryStartTime)
        : dayjs(order.createdTime);
    const end = dayjs(order.deliveryEndTime).isValid() ? dayjs(order.deliveryEndTime) : start;
    const dayCount = Math.max(end.diff(start, "day") + 1, 1);
    const totalPages = Math.ceil(dayCount / 15);
    const days = Array.from({ length: Math.min(15, Math.max(dayCount - (page - 1) * 15, 0)) }, (_, index) =>
        start.add((page - 1) * 15 + index, "day")
    );
    const schedulePayment = (row: ScheduleVo) => row.receivePayments?.find(item => item.scheduleId === row.schedule.id && item.paymentType === RECEIVE_PAYMENT_TYPE.ADVANCE_PAYMENT.code);
    const canConfirm = (row: ScheduleVo) => !((row.schedule.newDataFlag && order.orderType !== ORDER_TYPE_PC.PICKING_UP_MISSING_PRODUCTS.code)) && row.schedule.state === SCHEDULE_STATE.TO_BE_CONFIRMED.code && (!schedulePayment(row) || [PC_RECEIVE_STATE.PAID.code, PC_RECEIVE_STATE.OFFLINE_PAID.code].includes(schedulePayment(row)!.state));
    const canConfirmAndPay = (row: ScheduleVo) => !((row.schedule.newDataFlag && order.orderType !== ORDER_TYPE_PC.PICKING_UP_MISSING_PRODUCTS.code)) && row.schedule.state === SCHEDULE_STATE.TO_BE_CONFIRMED.code && order.productInfo?.settlementMethod !== SETTLEMENT_METHOD.FULL_PAYMENT.code && Boolean(row.receivePayments?.find(item => item.scheduleId === row.schedule.id && item.paymentType === RECEIVE_PAYMENT_TYPE.ADVANCE_PAYMENT.code && item.state === PC_RECEIVE_STATE.PENDING_PAYMENT.code));
    /** 确认排期成功后同时刷新订单主数据和当前排期页。 */
    const confirmSchedule = (row: ScheduleVo) => Modal.confirm({ title: "提示", content: "是否确认排期，确认后请等待提货", onOk: async () => { await scheduleService.confirm(row.schedule.id, { receivePaymentId: undefined }); void message.success("操作成功"); await Promise.all([Promise.resolve(onRefresh?.()), loadSchedules()]); } });
    const columns: TableColumnProps<ScheduleVo>[] = [
        { label: "排期编号", key: "id", width: 170, render: ({ row }) => row.schedule.id },
        ...(order.orderType === ORDER_TYPE_PC.PICKING_UP_MISSING_PRODUCTS.code ? [] : [{ label: "关联提货", key: "delivery", width: 170, render: ({ row }: { row: ScheduleVo }) => row.delivery?.id || "--" }]),
        {
            label: "排期日期",
            key: "scheduleDate",
            width: 130,
            render: ({ row }) => row.schedule.scheduleDate ? dayjs(row.schedule.scheduleDate).format("YYYY-MM-DD") : "--"
        },
        ...(order.orderType === ORDER_TYPE_PC.PICKING_UP_MISSING_PRODUCTS.code
            ? []
            : [{ label: "排期费用(元)", key: "loan", width: 150, render: ({ row }: { row: ScheduleVo }) => { const payment = schedulePayment(row); return payment ? `${money(payment.amount)}(${PC_RECEIVE_STATE.getIntroByCode(payment.state)})` : "--"; } }]),
        { label: "预估提货吨数(吨)", key: "estimatedTon", width: 150, render: ({ row }) => money(row.schedule.estimatedTon) },
        {
            label: "船舶",
            key: "ship",
            width: 200,
            render: ({ row }) => row.schedule.shipInfo ? <Button onClick={() => window.open(`/console/logistics/findLocation?mmsi=${row.schedule.shipId}`, "_blank")} type="link">{shipName(row.schedule.shipInfo, row.schedule.shipId)}</Button> : "--"
        },
        { label: "关联运单", key: "transportOrderShipId", width: 250, render: ({ row }) => row.transportOrderShip ? <Button onClick={() => window.open(`/console/logistics/shipOrder/detail?id=${row.transportOrderShip.id}`, "_blank")} type="link">{row.transportOrderShip.id} ({TRANSPORT_ORDER_SHIP_STATE.getIntroByCode(row.transportOrderShip.state)})</Button> : "--" },
        {
            label: "收货信息",
            key: "address",
            render: ({ row }) => <div className="order-schedule-address"><span>{addressText(row.schedule.addressInfo as unknown as Record<string, unknown>)}</span>{row.schedule.addressInfo?.geo ? <button aria-label="查看排期位置" onClick={() => setMapAddress(row.schedule.addressInfo)} type="button"><i className="iconfont icon-a-Frame1312316869-1" /></button> : null}</div>
        },
        {
            label: "排期状态",
            key: "state",
            width: 100,
            render: ({ row }) => SCHEDULE_STATE.getIntroByCode(row.schedule.state) ?? "--"
        },
        { label: "操作", key: "operation", fixed: "right", width: 200, showOverflowTooltip: false, render: ({ row }) => <div className="order-detail-links">{canConfirmAndPay(row) ? <Button onClick={() => { const payment = row.receivePayments.find(item => item.scheduleId === row.schedule.id && item.paymentType === RECEIVE_PAYMENT_TYPE.ADVANCE_PAYMENT.code && item.state === PC_RECEIVE_STATE.PENDING_PAYMENT.code); if (payment) setPaymentAction({ kind: "confirmPay", payment }); }} type="link">确认并支付</Button> : null}{canConfirm(row) ? <Button onClick={() => confirmSchedule(row)} type="link">确认排期</Button> : null}</div> }
    ];
    return (
        <Section id="order-detail-delivery" title="排期日历">
            <div className="order-schedule-page-buttons">
                <ZhihaoButton disabled={page <= 1} onClick={() => setPage(value => value - 1)}>
                    <i className="iconfont icon-left" />
                    上一页
                </ZhihaoButton>
                <ZhihaoButton disabled={page >= totalPages} onClick={() => setPage(value => value + 1)}>
                    下一页
                    <i className="iconfont icon-right" />
                </ZhihaoButton>
            </div>
            <div className="order-schedule-stats">
                <Grid cols={4}>
                    {[
                        { label: "可提货吨数（吨）", value: order.ton },
                        { label: "排期提货吨数（吨）", value: detail.scheduledDeliveryTon },
                        { label: "已提货吨数（吨）", value: detail.pickedUpTon },
                        { label: "剩余提货吨数（吨）", value: detail.remainPickedUpTon }
                    ].map(item => (
                        <BaseItem key={item.label} label={item.label} value={money(item.value)} />
                    ))}
                </Grid>
            </div>
            <div className="order-schedule-calendar">
                {days.map(date => {
                    const rows = (detail.schedules ?? []).filter(item => dayjs(item.scheduleDate).isSame(date, "day"));
                    return (
                        <div className="order-schedule-day" key={date.format("YYYY-MM-DD")}>
                            <div className="order-schedule-day__header">
                                <span>{date.format("YYYY-MM-DD")}</span>
                                <span>
                                    {["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"][date.day()]}
                                </span>
                                <strong>
                                    {date.isBefore(dayjs(), "day")
                                        ? "不可提货"
                                        : rows.length
                                          ? `已排期 ${money(rows.reduce((sum, item) => sum + Number(item.estimatedTon ?? 0), 0))}吨`
                                          : "预估可提"}
                                </strong>
                            </div>
                            <div className="order-schedule-day__content">
                                {rows.length ? (
                                    rows.slice(0, 3).map(item => (
                                        <div key={item.id}>
                                            <p>
                                                <span>排期编号</span>
                                                {item.id}
                                            </p>
                                            <p>
                                                <span>预估提货</span>
                                                {money(item.estimatedTon)}吨
                                            </p>
                                            <p>
                                                <span>排期状态</span>
                                                <em>{SCHEDULE_STATE.getIntroByCode(item.state)}</em>
                                            </p>
                                        </div>
                                    ))
                                ) : (
                                    <span className="order-schedule-day__empty">暂无排期</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <Table
                className="order-schedule-table"
                columns={columns}
                data={scheduleRows}
                empty={<EmptyState />}
                pagination={false}
                rowKey={row => row.schedule.id}
            />
            {scheduleTotal > 5 ? <div className="order-refund-pagination order-schedule-pagination"><span>共{scheduleTotal}条数据</span><Pagination current={listPage} pageSize={5} total={scheduleTotal} showQuickJumper showSizeChanger={false} onChange={setListPage} /></div> : null}
            <OrderPaymentDialogs action={paymentAction} onClose={() => setPaymentAction(undefined)} onSuccess={async () => { await Promise.all([Promise.resolve(onRefresh?.()), loadSchedules()]); }} order={order} />
            <AddressMapDialog address={mapAddress} label="排期收货位置" onClose={() => setMapAddress(undefined)} open={Boolean(mapAddress)} />
        </Section>
    );
}

/** 迁移自旧 deliveryDetail/deliveryCollapse.vue。 */
function DeliveryCollapse({ data, index }: { data: DeliveryVo; index: number }) {
    const [expanded, setExpanded] = useState(false);
    const [tab, setTab] = useState("delivery");
    const delivery = data.delivery;
    const tabs = [
        { value: "delivery", label: "提货信息" },
        { value: "proof", label: "佐证材料" }
    ];
    if (delivery.shipId || delivery.transportOrderShipId) tabs.push({ value: "logistics", label: "物流跟踪" });
    return (
        <div className="order-delivery-collapse">
            <button
                className="order-delivery-collapse__header"
                onClick={() => setExpanded(value => !value)}
                type="button"
            >
                <strong>提货{index}</strong>
                <span>排期编号</span>
                <span>{delivery.id || "--"}</span>
                <i className={`iconfont icon-down${expanded ? " rotate" : ""}`} />
            </button>
            <div className={`order-delivery-collapse__content${expanded ? " expanded" : ""}`}>
                <ZhInsetTabs active={tab} onChange={setTab} options={tabs} />
                {tab === "delivery" ? <DeliveryInfo data={data} embedded /> : null}
                {tab === "proof" ? <DeliveryProofMaterials data={data} embedded /> : null}
                {tab === "logistics" ? <DeliveryLogisticsTracking data={data} embedded /> : null}
            </div>
        </div>
    );
}

/** 迁移自旧 orderDetail/deliveryDetail.vue 与 deliveryTable.vue。 */
function OrderDeliveryList({ detail, onRefresh }: { detail: OrderVo; onRefresh?: () => Promise<void> | void }) {
    const navigate = useNavigate();
    const order = detail.order;
    const rows = detail.deliveryVos ?? [];
    const tableRef = useRef<TableRef<DeliveryVo>>(null);
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [confirming, setConfirming] = useState(false);
    const collapseMode =
        order.orderType === ORDER_TYPE_PC.PICKING_UP_MISSING_PRODUCTS.code &&
        [
            TRADE_ORDER_STATE_PC.IN_THE_PROCESS_OF_PICKING_UP_THE_GOODS.code,
            TRADE_ORDER_STATE_PC.COMPLETED.code
        ].includes(order.state);
    if (!rows.length) return null;
    if (collapseMode)
        return (
            <Section id="order-detail-delivery-list" title="提货明细">
                {rows.map((item, index) => (
                    <DeliveryCollapse data={item} index={index + 1} key={item.delivery?.id || index} />
                ))}
            </Section>
        );
    if (order.state === TRADE_ORDER_STATE_PC.TO_BE_PICKED_UP.code) return null;
    /** 单条与批量确认共用旧接口，成功后清空选择并重新加载订单。 */
    const confirm = (id?: string) => {
        const ids = id ? [id] : selectedIds.map(String);
        if (!ids.length) return;
        Modal.confirm({
            title: id ? "确认提货" : "批量确认提货",
            content: "是否确认提货",
            onOk: async () => {
                setConfirming(true);
                try {
                    await deliveryConfirm(ids);
                    setSelectedIds([]);
                    tableRef.current?.clearSelection();
                    void message.success("操作成功");
                    await onRefresh?.();
                } finally {
                    setConfirming(false);
                }
            }
        });
    };
    const columns: TableColumnProps<DeliveryVo>[] = [
        ...(order.state !== TRADE_ORDER_STATE_PC.COMPLETED.code ? [{ type: "selection" as const, width: 48, selectable: (row: DeliveryVo) => canConfirmDelivery(row) }] : []),
        {
            label: "提货编号",
            key: "id",
            width: 160,
            render: ({ row }) => (
                <Button type="link" onClick={() => navigate(`/orderCenter/myOrder/deliveryDetail/${row.delivery.id}`)}>
                    {row.delivery.id}
                </Button>
            )
        },
        {
            label: "提货日期",
            key: "date",
            width: 160,
            render: ({ row }) =>
                row.delivery.scheduleDate ? dayjs(row.delivery.scheduleDate).format("YYYY-MM-DD") : "--"
        },
        {
            label: "结算金额（元）",
            key: "amount",
            render: ({ row }) => money(row.delivery.settlementAmount)
        },
        {
            label: "提货吨位（吨）",
            key: "ton",
            render: ({ row }) => money(row.delivery.estimatedTon)
        },
        {
            label: "结算单价（元/吨）",
            key: "price",
            render: ({ row }) => money(row.delivery.settlementUnitAmount)
        },
        {
            label: "状态",
            key: "state",
            render: ({ row }) => DELIVERY_STATE_PC.getIntroByCode(row.delivery.state) ?? "--"
        },
        {
            label: "关联排期",
            key: "schedule",
            render: ({ row }) => row.delivery.scheduleId ?? "--"
        },
        {
            label: "操作",
            key: "operation",
            fixed: "right" as const,
            width: 300,
            showOverflowTooltip: false,
            render: ({ row }) =>
                canConfirmDelivery(row) ? (
                    <div className="order-delivery-operation">
                        <Button type="link" onClick={() => confirm(row.delivery.id)}>
                            确认提货
                        </Button>
                        <span>{deliveryRemainingTime(row.delivery.initiationTime)} 后自动确认提货</span>
                    </div>
                ) : null
        }
    ];
    return (
        <Section id="order-detail-delivery-list" title="提货明细">
            <div className="order-delivery-toolbar">
                {order.state !== TRADE_ORDER_STATE_PC.COMPLETED.code ? (
                    <Button
                        disabled={!selectedIds.length}
                        loading={confirming}
                        onClick={() => confirm()}
                        type="primary"
                    >
                        批量确认提货
                    </Button>
                ) : null}
            </div>
            <Table
                ref={tableRef}
                columns={columns}
                data={rows}
                pagination={false}
                rowKey={row => row.delivery.id}
                onSelectionChange={selected => setSelectedIds(selected.map(row => row.delivery.id))}
            />
        </Section>
    );
}

/** 订单详情的提货管理组件组，页面只传完整 OrderVo。 */
export function OrderDeliveryManage({
    detail,
    onRefresh
}: {
    detail: OrderVo;
    onRefresh?: () => Promise<void> | void;
}) {
    const order = detail.order;
    const showCalendar = !(
        order.state === TRADE_ORDER_STATE_PC.COMPLETED.code &&
        order.beforeFinishState === TRADE_ORDER_STATE_PC.TO_BE_PICKED_UP.code &&
        !(detail.schedules ?? []).length
    );
    return (
        <>
            <OrderDeliveryMessage detail={detail} />
            {showCalendar ? <OrderScheduleCalendar detail={detail} onRefresh={onRefresh} /> : null}
            <OrderDeliveryList detail={detail} onRefresh={onRefresh} />
        </>
    );
}
